import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import uuid from 'uuid/v4';
import {Map, Range} from 'immutable';
import {
  Popover,
  WithHoverAndFocus,
  Anchor,
  Checkbox as AtomCheckBox,
  Radio
} from '@albert-io/atomic';
import Checkbox from 'generic/Forms/Checkbox/Checkbox.react';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import SupplementManagerModalButton from 'client/Supplements/SupplementManager/SupplementManagerModalButton.react';
import {AuthoringQuestionModelV1} from 'resources/augmented/AuthoringQuestion/AuthoringQuestionModel.v1';
import AuthoringTextInput from 'client/EditPage/V2/AuthoringTextInput/AuthoringTextInput.react';

import constants from './V2/MultipleChoice.constants';
import './multiple-choice-editor.scss';

const minNumberOfOptions = 2;
const maxNumberOfOptions = 8;

const numberOfAnswersOptions = Range(minNumberOfOptions, maxNumberOfOptions + 1).map((val) =>
  Map({
    id: val,
    name: val
  })
);
/* eslint-disable react/prefer-stateless-function */
export default class MultipleChoiceQuestionEditor extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onBlur: PropTypes.func,
    onModelChange: PropTypes.func,
    validationErrors: ImmutablePropTypes.map
  };

  render() {
    return (
      <div className='multiplechoice-editor'>
        <MultipleChoiceEditorOptionInputs
          question={this.props.question}
          onBlur={this.props.onBlur}
          onModelChange={this.props.onModelChange}
          validationErrors={this.props.validationErrors}
        />
      </div>
    );
  }
}

class MultipleChoiceEditorOptionInputs extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onBlur: PropTypes.func,
    onModelChange: PropTypes.func,
    validationErrors: ImmutablePropTypes.map
  };

  /* eslint camelcase: ["error", {allow: ["UNSAFE_"]}] */
  UNSAFE_componentWillUpdate(nextProps) {
    const currentOptions = this.props.question.getOptions();
    const nextOptions = nextProps.question.getOptions();

    if (currentOptions !== nextOptions && nextOptions.isEmpty()) {
      this.setNumberOfOptions();
    }
  }

  setNumberOfOptions = (option) => {
    const numberOfOptions = option ? option.get('id') : 4;
    const options = this.props.question.getOptions().setSize(numberOfOptions);
    const newOption = new Map({
      label: '',
      value: ''
    });

    /* eslint-disable no-shadow */
    const updatedOptions = options.map((option) => option || newOption.set('id', uuid()));
    const validResponse = this.updateRubricValidResponse(updatedOptions);

    const updatedQuestion = this.props.question
      .setOptions(updatedOptions)
      .setValidResponse(validResponse);
    this.props.onModelChange(updatedQuestion);
  };

  setIsSelectMultiple = (e) => {
    let updatedQuestion = this.props.question.setIsSelectMultiple(e.target.checked);

    if (!e.target.checked) {
      updatedQuestion = updatedQuestion.setValidResponse(Map());
    }

    this.props.onModelChange(updatedQuestion);
  };

  updateRubricValidResponse(options) {
    const optionIds = options.map((option) => option.get('id'));
    const updatedRubric = this.props.question.getValidResponse().filter((response, key) => {
      return optionIds.includes(key);
    });

    return updatedRubric;
  }

  render() {
    const {question} = this.props;
    const options = question.getOptions();
    const isSelectMultiple = question.isSelectMultiple();

    return (
      <div>
        <SimpleDropdown
          label='Number of answers'
          activeItemId={options.size}
          options={numberOfAnswersOptions}
          storeName='NumberOfAnswersStore'
          onChange={this.setNumberOfOptions}
          destroyStoreOnUnmount
        />
        <Checkbox
          className='multiple-choice-editor__multiple-answers'
          checked={isSelectMultiple}
          onChange={this.setIsSelectMultiple}
          label='Multiple correct answers'
          name='isSelectMultiple'
        />
        <div>
          {this.props.validationErrors.get('rubric') ? (
            <div className='a-form-input__error'>{this.props.validationErrors.get('rubric')}</div>
          ) : (
            ''
          )}
          {options.map((option, index) => {
            return (
              <MultipleChoiceOption
                key={index}
                index={index}
                option={option}
                onBlur={this.props.onBlur}
                isSelectMultiple={isSelectMultiple}
                question={this.props.question}
                onModelChange={this.props.onModelChange}
                validationErrors={this.props.validationErrors}
              />
            );
          })}
        </div>
      </div>
    );
  }
}

class MultipleChoiceOption extends React.Component {
  static propTypes = {
    onBlur: PropTypes.func,
    option: PropTypes.object,
    index: PropTypes.number,
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onModelChange: PropTypes.func,
    validationErrors: ImmutablePropTypes.map
  };

  removeOptionRef = React.createRef();

  toggleOptionAsCorrect = () => {
    const optionId = this.props.option.get('id');
    const currentIds = this.props.question.getValidResponse();
    const newIds = currentIds.has(optionId)
      ? currentIds.remove(optionId)
      : currentIds.set(optionId, true);

    const updatedQuestion = this.props.question.setValidResponse(newIds);
    this.props.onModelChange(updatedQuestion, 'rubric');
  };

  markOptionAsCorrect = () => {
    const correctOption = Map({
      [this.props.option.get('id')]: true
    });

    const updatedQuestion = this.props.question.setValidResponse(correctOption);
    this.props.onModelChange(updatedQuestion, 'rubric');
  };

  onChange = (e) => {
    const options = this.props.question.getOptions();
    const updatedOptions = options.map((option) => {
      return option === this.props.option ? option.set('value', e.target.value) : option;
    });

    const updatedQuestion = this.props.question.setOptions(updatedOptions);
    this.props.onModelChange(updatedQuestion, 'options');
  };

  handleRemoveOption = (e) => {
    if (e.currentTarget.disabled) {
      return;
    }
    const {question, onModelChange} = this.props;
    const index = parseInt(e.currentTarget.dataset.index, 10);

    const options = question.getOptions();
    const optionToDeleteId = options.get(index).getId();
    const validResponse = question.getValidResponse();

    let updatedQuestion = question.setOptions(options.delete(index));
    const fieldsToValidate = ['options'];

    if (validResponse.has(optionToDeleteId)) {
      updatedQuestion = updatedQuestion.setValidResponse(validResponse.delete(optionToDeleteId));
      fieldsToValidate.push('rubric');
    }

    onModelChange(updatedQuestion, ...fieldsToValidate);
  };

  render() {
    const {question, option, index, onBlur, validationErrors} = this.props;
    const id = option.get('id');
    const value = option.get('value');
    const errors = validationErrors.getIn(['options', id], null);

    const validResponses = question.getValidResponse();
    const isCorrect = validResponses.has(id);
    const selectionType = question.isSelectMultiple() ? (
      <AtomCheckBox
        className='u-mar-r_1'
        label='Correct Answer'
        checked={isCorrect}
        onChange={this.toggleOptionAsCorrect}
      />
    ) : (
      <Radio className='u-mar-r_1' checked={isCorrect} onClick={this.markOptionAsCorrect} />
    );
    const pathToOption = ['options', `${index}`, 'value'];
    const isRemoveOptionDisabled = question.getOptions().size <= minNumberOfOptions;
    return (
      <div className='multiple-choice-editor-answer-option u-display_flex'>
        <b>{constants.MULTIPLE_CHOICE_LETTERS.charAt(index)})</b>

        <div className='multiple-choice-editor-answer-option-text-input'>
          <div className='multiple-choice-editor-answer-option-checkbox'>
            {selectionType}Correct answer
          </div>
          <AuthoringTextInput
            value={value}
            error={errors}
            onChange={this.onChange}
            onBlur={onBlur}
            data-field-path={pathToOption.join(',')}
          />
          <div className='multiple-choice-editor-answer-option-suplement-button u-mar-t_1'>
            <WithHoverAndFocus>
              {({hasHover, onMouseEnter, onMouseLeave}) => (
                <div
                  className='u-display_flex u-align-items_center u-color_red-500'
                  ref={this.removeOptionRef}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                >
                  <Anchor
                    as='button'
                    underlined
                    inheritColor
                    disabled={isRemoveOptionDisabled}
                    onClick={this.handleRemoveOption}
                    data-index={index}
                  >
                    Delete
                  </Anchor>
                  {hasHover && isRemoveOptionDisabled && (
                    <Popover expanded targetRef={this.removeOptionRef} position='bottom-start'>
                      Multiple Choice Questions must have at least two options.
                    </Popover>
                  )}
                </div>
              )}
            </WithHoverAndFocus>
            <SupplementManagerModalButton
              className='add-supplement-btn u-mar-b_2'
              linkButton
              color='blue'
              pathToProperty={pathToOption}
            />
          </div>
        </div>
      </div>
    );
  }
}
